import { useQuery } from 'react-query';
import backend from '../backend';

type Produto = {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  categoria: string;
  preco: string;
  promo?: string;
  estoque: number;
  status: boolean;
  vendas: number;
  canais: string[];
};

type Params = {
  busca?: string;
  status?: string;
  categoria?: string;
};

type ProdutoApiItem = {
  id?: number;
  nome?: string;
  sku?: string;
  marca?: string;
  categoria?: string;
  preco?: string;
  promo?: string;
  estoque?: number | string;
  status?: boolean | number | string | null;
  vendas?: number | string;
  canais?: string[];
};

async function getProdutos(params?: Params): Promise<Produto[]> {
  const { data } = await backend.get('/produtos', {
    params: {
      page: 1,
      per_page: 30,
      busca: params?.busca || '',
      status: params?.status || 'todos',
      categoria: params?.categoria || 'todas',
    },
  });

  const itens = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : [];

  const parseStatus = (value: ProdutoApiItem['status']): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim();
      return ['1', 'true', 'ativo', 'active', 'sim', 's'].includes(normalized);
    }
    return false;
  };

  return (itens as ProdutoApiItem[]).map((item, index: number) => ({
    id: item.id ?? index + 1,
    nome: item.nome || 'Produto sem nome',
    sku: item.sku || '-',
    marca: item.marca || '-',
    categoria: item.categoria || 'Acessórios',
    preco: item.preco || 'R$ 0,00',
    promo: item.promo,
    estoque: Number(item.estoque ?? 0),
    status: parseStatus(item.status),
    vendas: Number(item.vendas ?? 0),
    canais: Array.isArray(item.canais) ? item.canais : [],
  }));
}

export function useProdutos(params?: Params) {
  const busca = params?.busca || '';
  const status = params?.status || 'todos';
  const categoria = params?.categoria || 'todas';

  return useQuery(
    ['produtos', busca, status, categoria],
    () => getProdutos({ busca, status, categoria }),
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}
